import { orderBy } from 'lodash-es';
import keyBy from 'lodash-es/keyBy';
import { IState } from '..';
import { getSalesDistricts } from '../lookups/lookupsSelectors';
import { getProspectStateAndSalesDistrict } from '../prospects';
import {
  INewDealerRejection,
  INewDealerRequest,
  INewDealerRequestCart,
  INewDealerRequestCompleteEvent,
  INewDealerRequestEvent,
  INewDealerRequestMin,
  NewDealerEventTypes,
  NewDealerRequestCompleteEventRequest,
  NewDealerRequestEventRequest
} from './newDealerInterfaces';

export function getNewDealerRequestEvents(
  state: IState,
  newDealerRequestId: number,
  eventTypeId: NewDealerEventTypes
): { [index: number]: INewDealerRequestEvent } {
  const events = state.newDealer.events.filter(
    x => x.newDealerRequestId === newDealerRequestId && x.newDealerRequestEventTypeId === eventTypeId
  );

  return keyBy(events, (e: INewDealerRequestEvent) => e.newDealerRequestStateId);
}

export function getNewDealerRequestEvent(
  state: IState,
  { newDealerRequestEventTypeId, newDealerRequestId, newDealerRequestStateId }: NewDealerRequestEventRequest
): INewDealerRequestEvent | undefined {
  return state.newDealer.events.find(
    x =>
      x.newDealerRequestId === newDealerRequestId &&
      x.newDealerRequestEventTypeId === newDealerRequestEventTypeId &&
      x.newDealerRequestStateId === newDealerRequestStateId &&
      !x.newDealerRequestRejectionId
  );
}

export function getNewDealerRequestRejections(state: IState, newDealerRequestId: number): INewDealerRejection[] {
  const values = state.newDealer.requests[newDealerRequestId]
    ? state.newDealer.requests[newDealerRequestId].rejections
    : [];

  return orderBy<INewDealerRejection>(values, x => x.createdAtUtc).reverse();
}

export function getNewDealerRequestCompleteEvent(
  state: IState,
  { newDealerRequestCompleteEventTypeId, newDealerRequestId }: NewDealerRequestCompleteEventRequest
): INewDealerRequestCompleteEvent | undefined {
  return state.newDealer.completeEvents.find(
    x =>
      x.newDealerRequestId === newDealerRequestId &&
      x.newDealerRequestCompleteEventTypeId === newDealerRequestCompleteEventTypeId
  );
}

export function isNewDealerEventChecked(state: IState, request: NewDealerRequestEventRequest): boolean {
  const event = getNewDealerRequestEvent(state, request);

  return event ? true : false;
}

export function isNewDealerCompleteEventChecked(state: IState, request: NewDealerRequestCompleteEventRequest): boolean {
  const event = getNewDealerRequestCompleteEvent(state, request);

  return event ? true : false;
}

export function convertNewDealerRequestEventRequestToString({
  newDealerRequestEventTypeId,
  newDealerRequestId,
  newDealerRequestStateId
}: NewDealerRequestEventRequest): string {
  return `${newDealerRequestId}_${newDealerRequestStateId}_${newDealerRequestEventTypeId}`;
}

export function isNewDealerEventSaving(state: IState, request: NewDealerRequestEventRequest): boolean {
  return state.newDealer.savingEvents.includes(convertNewDealerRequestEventRequestToString(request));
}

export function getNewDealerRequestStateId(state: IState, request: { businessEntityId: number }): number {
  const newDealer = getNewDealerRequestByBusinessEntityId(state, request.businessEntityId);

  return newDealer ? newDealer.newDealerRequestStateId : -1;
}

export function getNewDealerProgramLevelId(
  state: IState,
  newDealerRequestId: number,
  brand: string
): number | undefined {
  return state.newDealer.carts[newDealerRequestId][brand]
    ? state.newDealer.carts[newDealerRequestId][brand].programLevelId
    : undefined;
}

export function getNewDealerCarts(state: IState, newDealerRequestId: number): INewDealerRequestCart[] {
  return state.newDealer.carts[newDealerRequestId] ? Object.values(state.newDealer.carts[newDealerRequestId]) : [];
}

export function getNewDealerCart(
  state: IState,
  newDealerRequestId: number,
  brand: string
): INewDealerRequestCart | undefined {
  let cart: INewDealerRequestCart | undefined;

  if (state.newDealer.carts[newDealerRequestId] && state.newDealer.carts[newDealerRequestId][brand]) {
    cart = state.newDealer.carts[newDealerRequestId][brand];
  }

  return cart;
}

export function getNewDealerRequestByBusinessEntityId(
  state: IState,
  businessEntityId: number
): INewDealerRequest | undefined {
  let request: INewDealerRequest | undefined;
  const newDealerRequestId = state.newDealer.businessEntityRequestMap[businessEntityId];

  if (newDealerRequestId) {
    request = state.newDealer.requests[newDealerRequestId];
  }

  return request;
}

export function getNewDealerRequest(state: IState, newDealerRequestId: number): INewDealerRequest | undefined {
  return state.newDealer.requests[newDealerRequestId];
}

export function getNewDealerProspectId(state: IState, newDealerReqeustId: number): number {
  const request = getNewDealerRequest(state, newDealerReqeustId);

  return request ? request.prospectId : -1;
}

export function getNewDealerBusinessEntityId(state: IState, newDealerReqeustId: number): number {
  const request = getNewDealerRequest(state, newDealerReqeustId);

  return request ? request.businessEntityId : -1;
}

export function getNewDealerBusinessName(state: IState, newDealerRequestId: number): string {
  let name = '';
  const beId = state.newDealer.requests[newDealerRequestId]
    ? state.newDealer.requests[newDealerRequestId].businessEntityId
    : undefined;

  if (beId) {
    name = state.businessEntity.names[beId];
  }

  return name;
}

export function getRejectedNewDealerEvents(
  state: IState,
  newDealerRequestRejectionId: number
): INewDealerRequestEvent[] {
  return state.newDealer.events.filter(x => x.newDealerRequestRejectionId === newDealerRequestRejectionId);
}

export function getNewDealerStateAndSalesDistrict(
  state: IState,
  newDealerRequestId: number
): { salesDistrict: string; state: string } {
  const prospectId = state.newDealer.requests[newDealerRequestId]
    ? state.newDealer.requests[newDealerRequestId].prospectId
    : -1;

  return getProspectStateAndSalesDistrict(state, prospectId);
}

export function getNewDealerRequestMinList(state: IState, type: 'global' | 'user'): INewDealerRequestMin[] {
  const salesDistricts = getSalesDistricts(state, type);

  return type === 'user'
    ? state.newDealer.requestMins.filter(x => salesDistricts.includes(x.salesDistrict))
    : state.newDealer.requestMins;
}
